// Peer-group classification: which scoring track a stock follows.
// Banks / life insurers / capital markets use bank-specific factors (NIM, efficiency, equity/assets).
// P&C insurers split into their own group (combined ratio, loss ratio, etc. — bank factors don't fit).
// Technology, energy, utility, REIT each score within their own peer group only.
// Everyone else is "standard."
//
// What changed 2026-06-15: removed non-loan-book industries from bank-like.
//   'Financial - Data & Stock Exchanges' → technology  (MCO, SPGI, NDAQ, CME, ICE: data platforms,
//      P/Book gave wrong targets because book value ≈ 0; they belong with P/E comps at tech premium)
//   'Asset Management' / 'Asset Management - Global' → standard  (BLK, KKR, BX, BAM, ARES, etc.:
//      fee businesses priced on earnings, not book)
//   'Insurance - Brokers' → standard  (AON, MRSH, WTW: pure-fee advisors with no underwriting risk)

// Industries that use bank-style financial factors. KEEP THIS NARROW: only businesses
// where book value (deposits, loan receivables, insurance reserves, trading assets) is
// the primary driver of value. P/Book ROE-regression is the pricing method for these names.
export const BANK_INDUSTRIES = new Set([
  // Commercial banks — loan books, deposits, NIM, NPL
  'Banks - Diversified',
  'Banks - Regional',
  'Banks - Regional - US',
  'Banks - Regional - Latin America',
  'Banks - Global',
  'Savings & Cooperative Banking',
  // Life insurance + diversified insurance: embedded-value reserves drive valuation;
  // no combined-ratio business model so P&C insurance group doesn't fit either.
  // They stay here as the least-bad option — the P/B ROE-regression is imperfect for
  // life insurance but better than applying standard ~18× to lumpy GAAP earnings.
  'Insurance - Life',
  'Insurance - Diversified',
  // Capital markets / investment banks: GS/MS have large trading-asset book values
  // that P/B prices correctly (−20% to −28% divergence vs street, acceptable).
  'Financial - Capital Markets',
  // Consumer credit / receivables books
  'Financial - Credit Services',
  'Financial - Mortgages',
  // Full-service investment banking with banking licences (IBKR)
  'Investment - Banking & Investment Services',
]);

// P&C insurance — its own peer group as of 2026-05-14. These names have an
// underwriting business model (premiums - claims - expenses = underwriting profit)
// where combined ratio / loss ratio / expense ratio / investment income ratio
// ARE the right Quality factors. Bank NIM/EFF/NPL don't apply.
export const INSURANCE_INDUSTRIES = new Set([
  'Insurance - Property & Casualty',
  'Insurance - Reinsurance',
  'Insurance - Specialty',
]);

export const ENERGY_INDUSTRIES = new Set([
  'Oil & Gas Exploration & Production',
  'Oil & Gas Integrated',
  'Oil & Gas Midstream',
  'Oil & Gas Refining & Marketing',
  'Oil & Gas Equipment & Services',
  'Uranium',
  'Solar',
]);

export const UTILITY_INDUSTRIES = new Set([
  'Regulated Electric',
  'Regulated Gas',
  'Regulated Water',
  'Diversified Utilities',
  'Independent Power Producers',
  'Renewable Utilities',
]);

export const REIT_INDUSTRIES = new Set([
  'REIT - Diversified',
  'REIT - Healthcare Facilities',
  'REIT - Hotel & Motel',
  'REIT - Industrial',
  'REIT - Office',
  'REIT - Residential',
  'REIT - Retail',
  'REIT - Specialty',
  'Real Estate - Services',
]);

// Technology companies — z-score within this group and earn a higher P/E cap
// (analysts apply ~28× to FY+1 EPS vs ~19× for the broader standard universe).
// Uses the same nonFinancial factor set as standard; peer-level z-scoring is the
// key difference. Semiconductors are included here for quality comparison but
// still get the 12× hard cap in multiple-resolver.ts.
//
// 'Financial - Data & Stock Exchanges' added 2026-06-15: exchanges (NDAQ, CME, ICE, CBOE)
// and rating/data agencies (MCO, SPGI, MORN) are platform monopolies priced at 22-33×
// forward earnings — identical to tech software multiples. P/Book was giving nonsense
// targets (MCO has near-zero tangible book → our target was $2 vs street $541).
export const TECHNOLOGY_INDUSTRIES = new Set([
  'Software - Application',
  'Software - Infrastructure',
  'Software - Services',
  'Software - Internet',
  'Semiconductors',
  'Semiconductor Equipment',
  'Information Technology Services',
  'Technology Distributors',
  'Computer Hardware',
  'Hardware, Equipment & Parts',
  'Electronic Components',
  'Communication Equipment',
  'Consumer Electronics',
  'Internet Content & Information',
  'Electronic Gaming & Multimedia',
  // Financial data platforms: exchanges + rating agencies — platform monopolies
  // with tech-level multiples; no loan book; P/E is the right method.
  'Financial - Data & Stock Exchanges',
]);

// Stocks that FMP classifies inside a BANK_INDUSTRIES bucket but should be priced
// on P/E (not P/Book) and scored with nonFinancial quality factors.
//
// V, MA — payment networks in 'Financial - Credit Services': no credit risk, no loan book,
//   asset-light networks. Kept out since founding.
// BRK-A/B — 'Insurance - Diversified' but is really a holding company; bank factors don't apply.
// AXP — 'Financial - Credit Services': holds receivables but earns on brand/rewards/spend volume;
//   P/B ROE-regression gives $183 vs street $367; P/E at standard ~22× gives ~$320, much closer.
// EVR, HLI — 'Financial - Capital Markets': pure M&A advisory boutiques, asset-light fee businesses.
// MKTX — 'Financial - Capital Markets': electronic bond trading platform (tech business model).
const NON_BANK_OVERRIDES = new Set(['BRK-A', 'BRK-B', 'V', 'MA', 'AXP', 'EVR', 'HLI', 'MKTX']);

export function isBankLike(stock: { symbol: string; industry: string }): boolean {
  if (NON_BANK_OVERRIDES.has(stock.symbol)) return false;
  return BANK_INDUSTRIES.has(stock.industry);
}

// Tighter subset of `isBankLike`: names that actually run a deposit + loan book
// (or consumer-receivables book), so the regulator-style bank factors —
// tier1CapRatio, nplRatio, provCoverage, rotce — are load-bearing. Excludes
// brokers (MS, GS), asset managers (BLK, TROW), exchanges (ICE, CME), and life
// insurers, which sit in the financial peer group for relative comparability
// but don't have a loan book for these factors to discriminate on.
const PROPER_BANK_INDUSTRIES = new Set([
  'Banks - Diversified',
  'Banks - Regional',
  'Banks - Regional - US',
  'Banks - Regional - Latin America',
  'Banks - Global',
  'Savings & Cooperative Banking',
  'Financial - Credit Services',  // COF/DFS/SYF/ALLY hold consumer receivables; AXP overridden out
  'Financial - Mortgages',
]);

export function isProperBank(stock: { symbol: string; industry: string }): boolean {
  if (NON_BANK_OVERRIDES.has(stock.symbol)) return false;
  return PROPER_BANK_INDUSTRIES.has(stock.industry);
}

export function isInsuranceLike(stock: { industry: string }): boolean {
  return INSURANCE_INDUSTRIES.has(stock.industry);
}

export function isEnergyLike(stock: { industry: string }): boolean {
  return ENERGY_INDUSTRIES.has(stock.industry);
}

export function isUtilityLike(stock: { industry: string }): boolean {
  return UTILITY_INDUSTRIES.has(stock.industry);
}

export function isREITLike(stock: { industry: string }): boolean {
  return REIT_INDUSTRIES.has(stock.industry);
}

// Payment networks: asset-light platform businesses with software-level margins and
// network-effect moats. V and MA have no loan book and no credit risk — they earn on
// transaction volume, not spread. Standard peer-median EV/EBITDA (~11×) is badly wrong
// for them; tech-peer multiples (20-28×) are the appropriate valuation frame.
const PAYMENT_NETWORK_OVERRIDES = new Set(['V', 'MA']);

// IT Services names that are distressed outsourcers or BPO companies trading at
// standard-market multiples (4-8× P/E) despite being in the "Information Technology
// Services" GICS industry. The technology peer group's 14-28× peer median overstates
// their fair value by 2-4×.
//
//  DXC  — DXC Technology (HP/CSC legacy outsourcing spinoff). ~4.9× street P/E.
//  KD   — Kyndryl (IBM infrastructure spinoff). ~7.4× street P/E.
//  CNXC — Concentrix (BPO/customer-experience outsourcer). ~4.2× street P/E.
//  CLVT — Clarivate (analytics / IP data, secular headwinds). ~4.3× street P/E.
const STANDARD_PEER_OVERRIDES = new Set(['DXC', 'KD', 'CNXC', 'CLVT']);

export function isTechnologyLike(stock: { symbol?: string; industry: string }): boolean {
  if (stock.symbol && PAYMENT_NETWORK_OVERRIDES.has(stock.symbol)) return true;
  if (stock.symbol && STANDARD_PEER_OVERRIDES.has(stock.symbol)) return false;
  return TECHNOLOGY_INDUSTRIES.has(stock.industry);
}

export type PeerGroup = 'financial' | 'insurance' | 'energy' | 'utility' | 'reit' | 'technology' | 'standard';

export function getPeerGroup(stock: { symbol: string; industry: string }): PeerGroup {
  if (isBankLike(stock)) return 'financial';
  if (isInsuranceLike(stock)) return 'insurance';
  if (isEnergyLike(stock)) return 'energy';
  if (isUtilityLike(stock)) return 'utility';
  if (isREITLike(stock)) return 'reit';
  if (isTechnologyLike(stock)) return 'technology';
  return 'standard';
}
